<template>
  <div class="tag-input">
    <div class="tag-content">
      <v-chip
        v-for="tag in selectedTags"
        :key="tag.text"
        closable
        class="tagChip"
        @click:close="remove(tag)"
      >
        {{ tag.text }}
      </v-chip>
      <div class="input-wrapper">
        <v-text-field
          ref="input"
          v-model="search"
          variant="plain"
          :flat="true"
          density="compact"
          :error-messages="errorMessage"
          class="tag-text-field"
          @keydown.enter.prevent="handleEnter"
          @keydown.backspace="handleBackspace"
          @update:model-value="showMenu = true"
          @blur="onBlur"
        />
        <div v-show="showMenu && matchingTags.length > 0" class="tag-menu">
          <div
            v-for="tag in matchingTags"
            :key="tag.text"
            class="tag-menu-item"
            @mousedown.prevent="selectTag(tag)"
          >
            {{ tag.text }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TagInput',
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    selectableTags: {
      type: Array,
      default: () => []
    },
    maxTags: {
      type: Number,
      default: 10
    },
    maxTagLength: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      selectedTags: [],
      availableTags: [],
      errorMessage: '',
      search: null,
      showMenu: false
    }
  },
  computed: {
    matchingTags() {
      if (!this.search) return [];
      const searchLower = this.search.toLowerCase()
      return this.availableTags.filter(tag => 
        tag.text.toLowerCase().includes(searchLower) &&
        !this.selectedTags.includes(tag)
      )
    }
  },
  created() {
    this.availableTags = this.selectableTags.map(tag => ({ text: tag }))
    this.selectedTags = this.modelValue.map(tag => ({ text: tag }))
  },

  watch: {
    modelValue: {
      handler(newValue) {
        // Update selectedTags when modelValue changes externally
        this.selectedTags = newValue.map(tag => ({ text: tag }))
      },
      immediate: true
    }
  },
  methods: {
    handleBackspace() {
      if (!this.search && this.selectedTags.length > 0) {
        const lastTag = this.selectedTags[this.selectedTags.length - 1]
        this.remove(lastTag)
      }
    },

    showMessage(message) {
      this.errorMessage = message
      setTimeout(() => this.errorMessage = '', 2000)
    },

    selectTag(tag) {
      if (this.selectedTags.length >= this.maxTags) {
        this.showMessage(`Maximum ${this.maxTags} tags allowed`);
        return
      }
      if (!this.selectedTags.includes(tag)) {
        this.selectedTags = [...this.selectedTags, tag]
        this.$emit('change', this.selectedTags.map(tag => tag.text))
      }
      this.search = ''
      this.showMenu = false
      this.errorMessage = ''
    },

    handleEnter() {
      if (this.selectedTags.length >= this.maxTags) {
        this.showMessage(`Maximum ${this.maxTags} tags allowed`);
        return
      }
      if (this.search.trim().length > this.maxTagLength) {
        this.showMessage(`Tag must be less than ${this.maxTagLength} characters`);
        return
      }
      if (this.search.trim()) {
        const searchTrim = this.search.trim()
        const exactMatch = this.matchingTags.find(tag => tag.text.toLowerCase() === searchTrim.toLowerCase())
        
        if (exactMatch) {
          this.selectTag(exactMatch)
        } else {
          const newTag = { text: searchTrim }
          if (this.availableTags.some(tag => tag.text.toLowerCase() === newTag.text.toLowerCase())) {
            this.showMessage('Tag already exists');
            return
          }

          this.availableTags.push(newTag)
          this.selectedTags = [...this.selectedTags, newTag]
          this.$emit('addedNew', newTag.text)
          this.$emit('change', this.selectedTags.map(tag => tag.text))
          this.search = ''
          this.showMenu = false
        }
      }
    },
    remove(item) {
      const index = this.selectedTags.indexOf(item)
      if (index >= 0) {
        this.selectedTags.splice(index, 1)
        this.$emit('change', this.selectedTags.map(tag => tag.text))
      }
    },

    onBlur() {
      setTimeout(() => {
        this.showMenu = false
      }, 200)
    }
  }
}
</script>

<style scoped>
.tag-input {
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  padding: 4px 8px;
  min-height: 56px;
  background: white;
}

.tag-content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-top: -5px;
  margin-bottom: -5px;
}

.input-wrapper {
  position: relative;
  flex: 1;
  min-width: 120px;
}

.tag-text-input {
  width: 100%;
  border: none;
  outline: none;
  font-size: 16px;
  padding: 4px;
  background: transparent;
}

.suggestion {
  position: absolute;
  left: 4px;
  color: rgba(0, 0, 0, 0.38);
  pointer-events: none;
  white-space: pre;
}

.tag-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 100;
}

.tag-menu-item {
  padding: 12px 16px;
  font-size: 16px;
  cursor: pointer;
}

.tag-menu-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.tagChip {
  margin: 5px;
  background: var(--background-color-alt3) !important;
  color: var(--text-color) !important;
}
</style>
